import {Box, Checkbox, FormControlLabel} from "@mui/material";
import { AddressField, schemaShape as addressFieldSchemaShape } from "components/AddressField";
import { useFormikContext } from "formik";
import React, { useEffect } from "react";
import { reportException } from "services/AppInsights";
import { formatAddress } from "services/QASService";
import * as yup from "yup";
import { InferType } from "yup";

export const schemaShape = (nationalAddresses = false) => ({
    addressForm: yup.object({
        residentialAddress: yup
            .object(addressFieldSchemaShape)
            .required("Please enter your residential address.")
            .test("residentialAddressValid", "Address is valid", async (value, context) => {
                if (value.format === "__incomplete") {
                    return context.createError({
                        path: "addressForm.residentialAddress",
                        message:
                            "Please enter and select your complete residential address from the list of suggestions.",
                    });
                }

                if (value.format && !nationalAddresses) {
                    try {
                        const { result } = await formatAddress(value.format);
                        if (result && result.address?.region !== "TAS") {
                            return context.createError({
                                path: "addressForm.residentialAddress",
                                message: "Please enter a Tasmanian address.",
                            });
                        }
                    } catch (error) {
                        reportException(error, { context: "AddressForm TAS validation" });
                        return context.createError({
                            path: "addressForm.residentialAddress",
                            message: "We couldn't verify this address. Please check your connection and try again.",
                        });
                    }
                }

                return true;
            }),
        postalAddress: yup
            .object(addressFieldSchemaShape)
            .nullable()
            .when("postalSameAsResidential", {
                is: false,
                then: (schema) =>
                    schema
                        .required("Please enter your postal address.")
                        .test("address", "complete", async (value, context) => {
                            if (value?.format === "__incomplete") {
                                return context.createError({
                                    path: "addressForm.postalAddress",
                                    message:
                                        "Please enter and select your complete postal address from the list of suggestions.",
                                });
                            }

                            return true;
                        }),
                otherwise: (schema) => schema.notRequired().nullable(),
            }),
        postalSameAsResidential: yup.boolean().default(true),
    }),
});

// Used only for type inference below; runtime schema is built per-form with schemaShape(nationalAddresses).
const schema = yup.object().shape(schemaShape());

type Values = InferType<typeof schema>;

interface AddressFormProps {
    nationalAddresses?: boolean;
}

export const AddressForm: React.FC<AddressFormProps> = ({ nationalAddresses = false }) => {
    const { values, errors, touched, handleChange, handleBlur, setFieldValue } = useFormikContext<Values>();
    useEffect(() => {
        if (values.addressForm.postalSameAsResidential) {
            setFieldValue(
                "addressForm.postalAddress",
                values.addressForm.residentialAddress ? { ...values.addressForm.residentialAddress } : null,
            );
        }
    }, [values.addressForm.postalSameAsResidential, values.addressForm.residentialAddress, setFieldValue]);

    useEffect(() => {
        if (values.addressForm.postalSameAsResidential === undefined) {
            setFieldValue("addressForm.postalSameAsResidential", true);
        }
    }, [setFieldValue, values.addressForm.postalSameAsResidential]);

    return (
        <Box sx={{ width: "100%", maxWidth: "475px" }}>
            <Box sx={{ marginBottom: 2 }}>
                <AddressField
                    name={"addressForm.residentialAddress"}
                    label={"Residential address"}
                    value={
                        values.addressForm.residentialAddress != null
                            ? { ...values.addressForm.residentialAddress, matched: [] }
                            : null
                    }
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.addressForm?.residentialAddress && Boolean(errors.addressForm?.residentialAddress)}
                    helperText={
                        touched.addressForm?.residentialAddress && errors.addressForm?.residentialAddress
                            ? String(errors.addressForm?.residentialAddress)
                            : nationalAddresses
                              ? undefined
                              : "Tasmanian addresses only"
                    }
                />
            </Box>
            <Box sx={{ marginBottom: 3 }}>
                <FormControlLabel
                    control={
                            <Checkbox
                                name="addressForm.postalSameAsResidential"
                                checked={values.addressForm.postalSameAsResidential}
                                onChange={handleChange}
                                color="primary"
                            />
                            }
                    label="My postal address is the same as my residential address"
                    sx={{
                        gap: 1,
                        "& .MuiFormControlLabel-label": {
                            color: "text.primary",
                            fontSize: "18px",
                            fontWeight: 400,
                            lineHeight: "26px",
                        },
                    }}
                />
            </Box>
            <Box>
                {!values.addressForm.postalSameAsResidential && (
                    <>
                        <AddressField
                            name={"addressForm.postalAddress"}
                            label={"Postal address"}
                            value={
                                values.addressForm.postalAddress != null
                                    ? { ...values.addressForm.postalAddress, matched: [] }
                                    : null
                            }
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.addressForm?.postalAddress && Boolean(errors.addressForm?.postalAddress)}
                            helperText={
                                touched.addressForm?.postalAddress && errors.addressForm?.postalAddress
                                    ? String(errors.addressForm?.postalAddress)
                                    : "Australian postal addresses only"
                            }
                            type="postal"
                        />
                    </>
                )}
            </Box>
        </Box>
    );
};
